import { useState, useCallback, useRef } from 'react';
import { Email } from '@/types/email';
import { ProcessingStatus, SenderUnsubscribeOutcome, UnsubscribeValue } from '@/types/email-list.types';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { toast } from 'sonner';
import { bulkEmailAction, handleUnsubscribe as handleUnsubscribeApi } from '@/lib/client/email';
import { EmailActionsClient } from '@/lib/cloudflare/email-actions';

interface UseEmailProcessingProps {
  setEmails: (emails: Email[] | ((prevEmails: Email[]) => Email[])) => void;
}

interface ProcessResult {
  successCount: number;
  failureCount: number;
  processedIds: Set<string>;
  senderPreferenceIds: string[];
  unsubscribeOutcomes: Record<SenderUnsubscribeOutcome, number>;
}

interface UndoState {
  action: 'delete' | 'archive' | 'unsubscribe';
  emails: Email[];
  emailIds: string[];
  senderPreferenceIds: string[];
  timestamp: number;
}

const UNDO_TIMEOUT = 10000; // 10 seconds to undo
const BATCH_SIZE = 20; // Process emails in batches for visible progress

function createEmptyUnsubscribeOutcomes(): Record<SenderUnsubscribeOutcome, number> {
  return {
    one_click_requested: 0,
    request_sent: 0,
    blocked_only: 0,
    request_failed: 0,
  };
}

function formatUnsubscribeToast(successCount: number, outcomes: Record<SenderUnsubscribeOutcome, number>): string {
  const parts: string[] = [];

  if (outcomes.one_click_requested > 0) {
    parts.push(`${outcomes.one_click_requested} Gmail-style one-click request${outcomes.one_click_requested === 1 ? '' : 's'}`);
  }
  if (outcomes.request_sent > 0) {
    parts.push(`${outcomes.request_sent} sender unsubscribe request${outcomes.request_sent === 1 ? '' : 's'} sent`);
  }
  if (outcomes.blocked_only > 0) {
    parts.push(`${outcomes.blocked_only} blocked in Winbox only`);
  }
  if (outcomes.request_failed > 0) {
    parts.push(`${outcomes.request_failed} sender request${outcomes.request_failed === 1 ? '' : 's'} could not be confirmed`);
  }

  const base = `Blocked ${successCount} email${successCount === 1 ? '' : 's'} in Winbox.`;
  return parts.length > 0 ? `${base} ${parts.join('. ')}.` : base;
}

export function useEmailProcessing({ setEmails }: UseEmailProcessingProps) {
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    action: null,
    total: 0,
    processed: 0
  });
  const [isUndoing, setIsUndoing] = useState(false);
  const undoStateRef = useRef<UndoState | null>(null);
  const undoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { user, accessToken, signIn } = useCFAuth();

  const processEmails = async (
    emailsToProcess: Email[],
    category: 'delete' | 'archive' | 'unsubscribe'
  ): Promise<ProcessResult | undefined> => {
    const allProcessedIds = new Set<string>();
    const failedIds = new Set<string>();
    const senderPreferenceIds = new Set<string>();
    const unsubscribeOutcomes = createEmptyUnsubscribeOutcomes();
    const errors: Record<string, string> = {};

    const handleUnsubscribe = async (emailId: string, unsubData: UnsubscribeValue) => {
      const { unsubscribeLink, method, formData, listUnsubscribeHeader, listUnsubscribePost } = unsubData;
      const userEmail = user?.email || '';

      try {
        const result = await handleUnsubscribeApi({
          emailId,
          method: method as 'none' | 'link' | 'post' | 'list-unsubscribe',
          unsubscribeLink,
          formData,
          listUnsubscribeHeader,
          listUnsubscribePost,
          userEmail,
        }, accessToken ?? undefined);

        console.log(`Unsubscribe result for ${emailId}:`, result.message);
        return {
          success: result.success,
          message: result.message,
          senderPreferenceId: result.senderPreferenceId,
          senderUnsubscribeOutcome: result.senderUnsubscribeOutcome,
        };
      } catch (error) {
        console.error(`Failed to unsubscribe ${emailId}:`, error);
        return {
          success: false,
          message: error instanceof Error ? error.message : 'Failed to unsubscribe',
          senderPreferenceId: null,
          senderUnsubscribeOutcome: 'request_failed' as const,
        };
      }
    };

    // Process emails in batches for visible progress updates
    const batches: Email[][] = [];
    for (let i = 0; i < emailsToProcess.length; i += BATCH_SIZE) {
      batches.push(emailsToProcess.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      const emailIds = batch.map(email => email.id);
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          const data = await bulkEmailAction(category, emailIds, accessToken ?? undefined);

          if (!data || !Array.isArray(data.results)) {
            console.error('Invalid response data:', data);
            throw new Error('Invalid response format from server');
          }

          const processResultPromises = data.results.map(async (result) => {
            if (!result || typeof result.emailId !== 'string') {
              return { type: 'invalid' as const };
            }

            if (result.status !== 'success') {
              return {
                type: 'error' as const,
                emailId: result.emailId,
                error: result.error || 'Unknown error',
              };
            }

            if (category === 'unsubscribe' && result.value) {
              const unsubscribeResult = await handleUnsubscribe(result.emailId, result.value);
              if (unsubscribeResult.success) {
                return {
                  type: 'success' as const,
                  emailId: result.emailId,
                  senderPreferenceId: unsubscribeResult.senderPreferenceId,
                  senderUnsubscribeOutcome: unsubscribeResult.senderUnsubscribeOutcome,
                };
              }

              return {
                type: 'unsubscribe-error' as const,
                emailId: result.emailId,
                error: unsubscribeResult.message || 'Failed to unsubscribe',
              };
            }

            return {
              type: 'success' as const,
              emailId: result.emailId,
            };
          });

          const processedResults = await Promise.all(processResultPromises);

          for (const processedResult of processedResults) {
            if (processedResult.type === 'invalid') {
              continue;
            }

            if (processedResult.type === 'error' || processedResult.type === 'unsubscribe-error') {
              failedIds.add(processedResult.emailId);
              errors[processedResult.emailId] = processedResult.error;
              continue;
            }

            allProcessedIds.add(processedResult.emailId);
            if (processedResult.senderPreferenceId) {
              senderPreferenceIds.add(processedResult.senderPreferenceId);
            }
            if (category === 'unsubscribe' && processedResult.senderUnsubscribeOutcome) {
              unsubscribeOutcomes[processedResult.senderUnsubscribeOutcome] += 1;
            }
          }

          // Update progress after each batch completes
          setProcessingStatus(prev => ({
            ...prev,
            processed: allProcessedIds.size + failedIds.size
          }));

          break; // Success, exit retry loop
        } catch (error) {
          console.error(`Processing error (attempt ${retryCount + 1}):`, error);

          if (error instanceof Error && error.message.includes('401')) {
            signIn();
            return undefined;
          }

          if (retryCount === maxRetries - 1) {
            batch.forEach(email => {
              failedIds.add(email.id);
              errors[email.id] = error instanceof Error ? error.message : 'Unknown error';
            });
            // Update progress even on failure
            setProcessingStatus(prev => ({
              ...prev,
              processed: allProcessedIds.size + failedIds.size
            }));
          } else {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
          }
        }
      }
    }

    return {
      successCount: allProcessedIds.size,
      failureCount: failedIds.size,
      processedIds: allProcessedIds,
      senderPreferenceIds: Array.from(senderPreferenceIds),
      unsubscribeOutcomes,
    };
  };

  const handleUndo = useCallback(async () => {
    const undoState = undoStateRef.current;
    if (!undoState || isUndoing || !accessToken) {
      return;
    }

    // Check if undo is still valid (within timeout)
    if (Date.now() - undoState.timestamp > UNDO_TIMEOUT) {
      toast.error('Undo expired');
      undoStateRef.current = null;
      return;
    }

    setIsUndoing(true);

    try {
      const client = new EmailActionsClient(accessToken);
      const result = await client.undoAction(
        undoState.action,
        undoState.emailIds,
        undoState.senderPreferenceIds
      );

      if (result.summary.successCount > 0) {
        // Restore emails to UI
        setEmails(prevEmails => [...undoState.emails, ...prevEmails]);

        const actionVerb = undoState.action === 'delete' ? 'deletion' :
                          undoState.action === 'archive' ? 'archival' : 'unsubscription';

        if (undoState.action === 'unsubscribe') {
          toast.success(
            `Restored ${result.summary.successCount} email${result.summary.successCount > 1 ? 's' : ''} to inbox. Any sender-side unsubscribe requests may still continue.`
          );
        } else {
          toast.success(
            `Undid ${actionVerb} of ${result.summary.successCount} email${result.summary.successCount > 1 ? 's' : ''}`
          );
        }
      }

      if (result.summary.errorCount > 0) {
        toast.error(`Failed to undo ${result.summary.errorCount} email${result.summary.errorCount > 1 ? 's' : ''}`);
      }

      // Clear undo state
      undoStateRef.current = null;
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
        undoTimeoutRef.current = null;
      }

    } catch (error) {
      console.error('Undo error:', error);
      toast.error('Failed to undo action');
    } finally {
      setIsUndoing(false);
    }
  }, [accessToken, isUndoing, setEmails]);

  const handleProcessing = useCallback(async (
    emails: Email[],
    action: 'delete' | 'archive' | 'unsubscribe'
  ) => {
    if (processingStatus.action) return;
    if (!user?.email) {
      console.error('No user email found');
      signIn();
      return;
    }

    try {
      setProcessingStatus({
        action,
        total: emails.length,
        processed: 0
      });

      const result = await processEmails(emails, action);

      if (result) {
        // Store undo state before removing emails from UI
        const processedEmails = emails.filter(e => result.processedIds.has(e.id));
        undoStateRef.current = {
          action,
          emails: processedEmails,
          emailIds: Array.from(result.processedIds),
          senderPreferenceIds: result.senderPreferenceIds,
          timestamp: Date.now(),
        };

        // Clear any existing undo timeout
        if (undoTimeoutRef.current) {
          clearTimeout(undoTimeoutRef.current);
        }

        // Set timeout to clear undo state
        undoTimeoutRef.current = setTimeout(() => {
          undoStateRef.current = null;
        }, UNDO_TIMEOUT);

        // Update the UI to remove processed emails
        setEmails(prevEmails => {
          const processedIds = new Set(result.processedIds);
          return prevEmails.filter(email => !processedIds.has(email.id));
        });

        // Show toast notification with undo button
        if (result.successCount > 0) {
          const successMessage = action === 'unsubscribe'
            ? formatUnsubscribeToast(result.successCount, result.unsubscribeOutcomes)
            : `Successfully ${action === 'delete' ? 'deleted' : 'archived'} ${result.successCount} email${result.successCount > 1 ? 's' : ''}`;
          toast.success(successMessage, {
            duration: UNDO_TIMEOUT,
            action: {
              label: 'Undo',
              onClick: () => handleUndo(),
            },
          });
        }

        if (result.failureCount > 0) {
          toast.error(`Failed to process ${result.failureCount} email${result.failureCount > 1 ? 's' : ''}`);
        }
      }

    } catch (error) {
      console.error('Processing error:', error);
      toast.error('Failed to process emails');
    } finally {
      setProcessingStatus({
        action: null,
        total: 0,
        processed: 0
      });
    }
  }, [processingStatus.action, user, accessToken, signIn, setEmails]);

  const cancelProcessing = useCallback(() => {
    setProcessingStatus({
      action: null,
      total: 0,
      processed: 0
    });
  }, []);

  return {
    processingStatus,
    handleProcessing,
    cancelProcessing,
    handleUndo,
    isUndoing,
  };
}
